/**
 * Baked material tiles — the Canvas2D path (ui-ux §19.1 fallback list, §19.0.2; GDD §10.1.3, C143). Owner: UI/Visual (F3).
 *
 * Gold, lapis and vermilion never appear as CSS gradients (`mc/no-gradient-gold`): every component paints them from
 * these seamless tiles. Art §4.11 may later bake the same tiles on the GPU and call {@link setMaterialUrls}; until then
 * this module bakes all of them deterministically from a seed in a few milliseconds.
 *
 * Tiles: vellum 512² · gold 256² (craquelure, leaf seams, speckle) + sheen mask · lapis 256² · lapisNight 256² ·
 * vermilion 256² (tempera strokes) · verdigris 256² · desk stars 96² · fibre noise 128² (the art reveal mask).
 */
import { Rng } from '../shared';
import { PIGMENTS, hexToRgb } from './tokens';

type RGB = [number, number, number];

export interface BakeResult { ms: number; urls: Record<string, string> }

/** CSS custom property → baked tile name. */
const VARS: Record<string, string> = {
  '--mc-vellum-tex': 'vellum', '--mc-gold-tex': 'gold', '--mc-gold-sheen': 'sheen', '--mc-lapis-tex': 'lapis',
  '--mc-lapis-night-tex': 'lapisNight', '--mc-vermilion-tex': 'vermilion', '--mc-verdigris-tex': 'verdigris',
  '--desk-stars': 'stars', '--mc-noise-tex': 'noise',
};

// ── tileable noise ────────────────────────────────────────────────────────────────────────────────────────────────

/** Periodic value noise on a `period`-cell lattice spanning a `size`-px tile. */
function valueNoise(rng: Rng, size: number, period: number): (x: number, y: number) => number {
  const lat = new Float32Array(period * period);
  for (let i = 0; i < lat.length; i++) lat[i] = rng.float();
  const cell = size / period;
  const s = (t: number): number => t * t * (3 - 2 * t);
  return (x, y) => {
    const fx = x / cell, fy = y / cell;
    const x0 = Math.floor(fx), y0 = Math.floor(fy);
    const tx = s(fx - x0), ty = s(fy - y0);
    const i0 = ((x0 % period) + period) % period, j0 = ((y0 % period) + period) % period;
    const i1 = (i0 + 1) % period, j1 = (j0 + 1) % period;
    const a = lat[j0 * period + i0], b = lat[j0 * period + i1], c = lat[j1 * period + i0], d = lat[j1 * period + i1];
    return (a + (b - a) * tx) + ((c + (d - c) * tx) - (a + (b - a) * tx)) * ty;
  };
}

/** Anisotropic periodic noise (fibres, brush strokes): separate lattice periods in x and y. */
function stretchedNoise(rng: Rng, size: number, px: number, py: number): (x: number, y: number) => number {
  const lat = new Float32Array(px * py);
  for (let i = 0; i < lat.length; i++) lat[i] = rng.float();
  const cx = size / px, cy = size / py;
  const s = (t: number): number => t * t * (3 - 2 * t);
  return (x, y) => {
    const fx = x / cx, fy = y / cy;
    const x0 = Math.floor(fx), y0 = Math.floor(fy);
    const tx = s(fx - x0), ty = s(fy - y0);
    const i0 = ((x0 % px) + px) % px, j0 = ((y0 % py) + py) % py, i1 = (i0 + 1) % px, j1 = (j0 + 1) % py;
    const a = lat[j0 * px + i0], b = lat[j0 * px + i1], c = lat[j1 * px + i0], d = lat[j1 * px + i1];
    return (a + (b - a) * tx) + ((c + (d - c) * tx) - (a + (b - a) * tx)) * ty;
  };
}

function fbm(rng: Rng, size: number, periods: number[], weights: number[]): (x: number, y: number) => number {
  const layers = periods.map((p) => valueNoise(rng, size, p));
  const wsum = weights.reduce((a, b) => a + b, 0);
  return (x, y) => { let v = 0; for (let i = 0; i < layers.length; i++) v += layers[i](x, y) * weights[i]; return v / wsum; };
}

/** Jittered-grid periodic Voronoi: returns (d2 − d1) at a pixel (small near cell borders = the cracks). */
function voronoiEdge(rng: Rng, size: number, cells: number): (x: number, y: number) => number {
  const pts = new Float32Array(cells * cells * 2);
  const cs = size / cells;
  for (let j = 0; j < cells; j++) for (let i = 0; i < cells; i++) {
    pts[(j * cells + i) * 2] = (i + 0.15 + 0.7 * rng.float()) * cs;
    pts[(j * cells + i) * 2 + 1] = (j + 0.15 + 0.7 * rng.float()) * cs;
  }
  return (x, y) => {
    const ci = Math.floor(x / cs), cj = Math.floor(y / cs);
    let d1 = 1e9, d2 = 1e9;
    for (let dj = -1; dj <= 1; dj++) for (let di = -1; di <= 1; di++) {
      const ii = ci + di, jj = cj + dj;
      const wi = ((ii % cells) + cells) % cells, wj = ((jj % cells) + cells) % cells;
      const px = pts[(wj * cells + wi) * 2] + (ii - wi) * cs, py = pts[(wj * cells + wi) * 2 + 1] + (jj - wj) * cs;
      const d = Math.hypot(px - x, py - y);
      if (d < d1) { d2 = d1; d1 = d; } else if (d < d2) d2 = d;
    }
    return d2 - d1;
  };
}

const mix = (a: RGB, b: RGB, t: number): RGB => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
const scale = (a: RGB, k: number): RGB => [a[0] * k, a[1] * k, a[2] * k];
const rgb = (hex: string): RGB => hexToRgb(hex) as RGB;

// ── tile painters (pure: size + rng → RGBA bytes) ─────────────────────────────────────────────────────────────────

type Painter = (rng: Rng, size: number) => Uint8ClampedArray;

function fill(size: number, fn: (x: number, y: number) => RGB | [number, number, number, number]): Uint8ClampedArray {
  const out = new Uint8ClampedArray(size * size * 4);
  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
    const c = fn(x + 0.5, y + 0.5);
    const o = (y * size + x) * 4;
    out[o] = c[0]; out[o + 1] = c[1]; out[o + 2] = c[2]; out[o + 3] = c.length === 4 ? c[3] : 255;
  }
  return out;
}

/** Dots stamped with wrap-around into an RGBA buffer (alpha-over). */
function stamp(buf: Uint8ClampedArray, size: number, cx: number, cy: number, r: number, col: RGB, a: number): void {
  const r2 = r + 1;
  for (let y = Math.floor(cy - r2); y <= Math.ceil(cy + r2); y++) for (let x = Math.floor(cx - r2); x <= Math.ceil(cx + r2); x++) {
    const d = Math.hypot(x + 0.5 - cx, y + 0.5 - cy);
    const cov = Math.max(0, Math.min(1, r + 0.5 - d)) * a;
    if (cov <= 0) continue;
    const wx = ((x % size) + size) % size, wy = ((y % size) + size) % size;
    const o = (wy * size + wx) * 4;
    buf[o] += (col[0] - buf[o]) * cov; buf[o + 1] += (col[1] - buf[o + 1]) * cov; buf[o + 2] += (col[2] - buf[o + 2]) * cov;
  }
}

const vellum: Painter = (rng, size) => {
  const base = rgb(PIGMENTS.vellum), aged = rgb(PIGMENTS.vellumAged), shadow = rgb(PIGMENTS.vellumShadow), light = rgb(PIGMENTS.vellumLight);
  const mottle = fbm(rng.stream('m'), size, [4, 8, 16, 32], [1, 0.6, 0.35, 0.2]);
  const fibre = stretchedNoise(rng.stream('f'), size, 6, 160);
  const fine = valueNoise(rng.stream('g'), size, 128);
  const buf = fill(size, (x, y) => {
    const m = mottle(x, y);
    let c = m > 0.5 ? mix(base, aged, Math.min(0.5, (m - 0.5) * 1.3)) : mix(base, light, Math.min(0.45, (0.5 - m) * 1.1));
    c = scale(c, 1 + (fibre(x, y) - 0.5) * 0.07 + (fine(x, y) - 0.5) * 0.025);
    return c;
  });
  const dots = rng.stream('d');
  for (let i = 0; i < size * size / 2600; i++) stamp(buf, size, dots.float() * size, dots.float() * size, 0.45 + dots.float() * 0.7, shadow, 0.25 + dots.float() * 0.3);
  return buf;
};

const gold: Painter = (rng, size) => {
  const g = rgb(PIGMENTS.gold), gl = rgb(PIGMENTS.goldLight), gd = rgb(PIGMENTS.goldDeep), bole = rgb(PIGMENTS.bole);
  const sheen = fbm(rng.stream('s'), size, [2, 4, 8], [1, 0.5, 0.25]);
  const crack = voronoiEdge(rng.stream('c'), size, 7);
  const crackFine = voronoiEdge(rng.stream('cf'), size, 16);
  const speck = valueNoise(rng.stream('p'), size, 64);
  const leaf = 64;                                      // gold leaf squares: faint seams every 64 px (4 per tile)
  const jit = stretchedNoise(rng.stream('j'), size, 4, 4);
  return fill(size, (x, y) => {
    const s = sheen(x, y);
    let c = s > 0.5 ? mix(g, gl, Math.min(0.55, (s - 0.5) * 1.6)) : mix(g, gd, Math.min(0.35, (0.5 - s) * 1.1));
    const e = crack(x, y), ef = crackFine(x, y);
    if (e < 1.6) c = mix(c, mix(gd, bole, 0.35), 0.38 * (1 - e / 1.6));
    else if (ef < 0.9) c = mix(c, gd, 0.22 * (1 - ef / 0.9));
    const sx = (x + jit(x, y) * 3) % leaf, sy = (y + jit(y, x) * 3) % leaf;
    if (sx < 0.9 || sy < 0.9) c = mix(c, gd, 0.16);
    else if (sx < 1.8 || sy < 1.8) c = mix(c, gl, 0.18);
    const p = speck(x, y);
    if (p > 0.78) c = mix(c, gl, (p - 0.78) * 0.9);
    return c;
  });
};

/** The sheen mask: a soft diagonal band of alpha (used as `mask-image`). */
const sheenMask: Painter = (_rng, size) => fill(size, (x, y) => {
  const d = (x - size / 2) + (y - size / 2) * 0.35;
  const a = Math.exp(-(d * d) / (2 * (size * 0.09) ** 2));
  return [255, 255, 255, Math.round(255 * a)];
});

const granular = (pig: RGB, dark: RGB, light: RGB, amp: number): Painter => (rng, size) => {
  const mottle = fbm(rng.stream('m'), size, [4, 8, 16], [1, 0.5, 0.3]);
  const grain = valueNoise(rng.stream('g'), size, 128);
  const fine = valueNoise(rng.stream('h'), size, 256);
  const buf = fill(size, (x, y) => {
    const m = mottle(x, y) - 0.5;
    let c = scale(pig, 1 + m * amp * 1.6);
    const gr = grain(x, y) * 0.6 + fine(x, y) * 0.4;
    if (gr > 0.66) c = mix(c, light, (gr - 0.66) * 0.55);
    if (gr < 0.3) c = mix(c, dark, (0.3 - gr) * 0.7);
    return c;
  });
  const dots = rng.stream('d');
  for (let i = 0; i < size * size / 900; i++) stamp(buf, size, dots.float() * size, dots.float() * size, 0.4 + dots.float() * 0.5, dots.chance(0.5) ? dark : light, 0.18 + dots.float() * 0.2);
  return buf;
};

const lapis = granular(rgb(PIGMENTS.lapis), rgb(PIGMENTS.lapisNight), rgb(PIGMENTS.azurite), 0.12);
const lapisNight = granular(rgb(PIGMENTS.lapisNight), rgb('#0A1338'), rgb(PIGMENTS.lapis), 0.14);
const verdigris = granular(rgb(PIGMENTS.verdigris), rgb(PIGMENTS.verdigrisDeep), rgb(PIGMENTS.verdigrisLight), 0.08);

const vermilion: Painter = (rng, size) => {
  const v = rgb(PIGMENTS.vermilion), r = rgb(PIGMENTS.rubric), m = rgb(PIGMENTS.minium);
  const stroke = stretchedNoise(rng.stream('s'), size, 32, 4);
  const stroke2 = stretchedNoise(rng.stream('t'), size, 6, 48);
  const mottle = fbm(rng.stream('m'), size, [4, 8], [1, 0.5]);
  return fill(size, (x, y) => {
    // horizontal tempera strokes (an anisotropic lattice keeps the tile seamless) plus a few cross-hatched dabs
    const s = stroke(x, y) * 0.6 + stroke2(x, y) * 0.4 - 0.5;
    const t = mottle(x, y) - 0.5;
    let c = v;
    c = s > 0 ? mix(c, m, s * 0.16) : mix(c, r, -s * 0.22);
    return scale(c, 1 + t * 0.05);
  });
};

/** Desk background stars (ui-ux §19.1): 5 gold dots at 60 % + one 4-point star on transparent. */
const stars: Painter = (rng, size) => {
  const buf = new Uint8ClampedArray(size * size * 4);
  const gold3 = rgb(PIGMENTS.gold), gl = rgb(PIGMENTS.goldLight);
  const put = (x: number, y: number, col: RGB, a: number): void => {
    const wx = ((Math.round(x) % size) + size) % size, wy = ((Math.round(y) % size) + size) % size;
    const o = (wy * size + wx) * 4;
    const na = Math.min(255, buf[o + 3] + a * 255);
    buf[o] = col[0]; buf[o + 1] = col[1]; buf[o + 2] = col[2]; buf[o + 3] = na;
  };
  for (let i = 0; i < 5; i++) {
    const cx = rng.float() * size, cy = rng.float() * size, r = 0.8 + rng.float() * 0.7;
    for (let y = -2; y <= 2; y++) for (let x = -2; x <= 2; x++) { const d = Math.hypot(x, y); if (d <= r + 0.5) put(cx + x, cy + y, gold3, 0.6 * Math.min(1, r + 0.5 - d)); }
  }
  const sx = size * 0.62, sy = size * 0.38;
  for (let k = -6; k <= 6; k++) {
    const a = 1 - Math.abs(k) / 7;
    put(sx + k, sy, gl, 0.85 * a); put(sx, sy + k, gl, 0.85 * a);
    if (Math.abs(k) <= 2) { put(sx + k, sy + k, gl, 0.4 * a); put(sx + k, sy - k, gl, 0.4 * a); }
  }
  return buf;
};

/** Fibrous alpha noise for the ink-bleed reveal of painted art (art §15.3 step 4). */
const noise: Painter = (rng, size) => {
  const n = fbm(rng.stream('n'), size, [8, 16, 32, 64], [1, 0.6, 0.4, 0.3]);
  const f = stretchedNoise(rng.stream('f'), size, 4, 64);
  return fill(size, (x, y) => { const v = n(x, y) * 0.75 + f(x, y) * 0.25; return [255, 255, 255, Math.round(255 * v)]; });
};

const TILES: Record<string, { size: number; paint: Painter }> = {
  vellum: { size: 512, paint: vellum }, gold: { size: 256, paint: gold }, sheen: { size: 128, paint: sheenMask },
  lapis: { size: 256, paint: lapis }, lapisNight: { size: 256, paint: lapisNight }, vermilion: { size: 256, paint: vermilion },
  verdigris: { size: 256, paint: verdigris }, stars: { size: 96, paint: stars }, noise: { size: 128, paint: noise },
};

/// ── 9-slice frames (Canvas2D, composed from the baked tiles) ──────────────────────────────────────────────────────
// Stand-ins for F5's painted `ui/frames/*` until the manifest provides them (ui-ux §19.0.3 rule 3 fallback, drawn
// richer than the flat CSS fallback: gold-leaf band with punchwork, vermilion pearl band, quatrefoil corners).

type Ctx = CanvasRenderingContext2D;

function quatrefoil(ctx: Ctx, cx: number, cy: number, r: number): void {
  ctx.beginPath();
  for (let i = 0; i < 4; i++) {
    const a = i * Math.PI / 2;
    const ox = cx + Math.cos(a) * r * 0.55, oy = cy + Math.sin(a) * r * 0.55;
    ctx.moveTo(ox + r * 0.45, oy);
    ctx.arc(ox, oy, r * 0.45, 0, Math.PI * 2);
  }
}

/** Draws a band layer of width `w` at inset `at` around a `size` square (four strips). */
function band(ctx: Ctx, size: number, at: number, w: number, style: string | CanvasPattern): void {
  ctx.fillStyle = style;
  ctx.fillRect(at, at, size - 2 * at, w);
  ctx.fillRect(at, size - at - w, size - 2 * at, w);
  ctx.fillRect(at, at + w, w, size - 2 * at - 2 * w);
  ctx.fillRect(size - at - w, at + w, w, size - 2 * at - 2 * w);
}

const inCorner = (x: number, y: number, S: number, B: number): boolean =>
  (x < B || x > S - B) && (y < B || y > S - B);

/** Gilt Illuminated frame: 192² source, slice 48 (= 16 CSS px band at 3× density). */
function paintGilt(gold: HTMLCanvasElement, lapis: HTMLCanvasElement): HTMLCanvasElement {
  const S = 192, B = 48;
  const c = document.createElement('canvas'); c.width = S; c.height = S;
  const ctx = c.getContext('2d')!;
  const gp = ctx.createPattern(gold, 'repeat')!, lp = ctx.createPattern(lapis, 'repeat')!;
  const P = PIGMENTS;
  band(ctx, S, 0, 3, P.ink);
  band(ctx, S, 3, 23, gp);
  band(ctx, S, 26, 3, P.ink);
  band(ctx, S, 29, 12, P.vermilion);
  band(ctx, S, 41, 3, P.ink);
  band(ctx, S, 44, 4, P.goldLight);
  // punchwork: a row of tooled dots along the gold band (every 8 px → tiles cleanly on the 96 px middle)
  ctx.fillStyle = 'rgba(92,58,34,.55)';
  for (let t = 4; t < S; t += 8) for (const [x, y] of [[t, 14.5], [t, S - 14.5], [14.5, t], [S - 14.5, t]]) {
    if (inCorner(x, y, S, B)) continue;
    ctx.beginPath(); ctx.arc(x, y, 1.6, 0, Math.PI * 2); ctx.fill();
  }
  // lead-white pearls on the vermilion band (every 12 px)
  for (let t = 6; t < S; t += 12) for (const [x, y] of [[t, 35], [t, S - 35], [35, t], [S - 35, t]]) {
    if (inCorner(x, y, S, B)) continue;
    ctx.beginPath(); ctx.arc(x, y, 3.6, 0, Math.PI * 2);
    ctx.fillStyle = P.leadWhite; ctx.fill(); ctx.lineWidth = 1.2; ctx.strokeStyle = P.ink; ctx.stroke();
  }
  // corner blocks: lapis square with a gold quatrefoil (keyart corners)
  for (const [x, y] of [[0, 0], [S - B, 0], [0, S - B], [S - B, S - B]]) {
    ctx.fillStyle = P.ink; ctx.fillRect(x, y, B, B);
    ctx.fillStyle = lp; ctx.fillRect(x + 3, y + 3, B - 6, B - 6);
    ctx.lineWidth = 2; ctx.strokeStyle = P.goldLight; ctx.strokeRect(x + 6, y + 6, B - 12, B - 12);
    quatrefoil(ctx, x + B / 2, y + B / 2, 15);
    ctx.fillStyle = gp; ctx.fill(); ctx.lineWidth = 2.2; ctx.strokeStyle = P.ink; ctx.stroke();
    ctx.beginPath(); ctx.arc(x + B / 2, y + B / 2, 3.2, 0, Math.PI * 2); ctx.fillStyle = P.vermilion; ctx.fill(); ctx.stroke();
  }
  return c;
}

/** Plate band («Главное», header plates): 96² source, slice 24 → 6 px band; ink rim, red bole, tooled gold. */
function paintPlateBand(gold: HTMLCanvasElement): HTMLCanvasElement {
  const S = 96;
  const c = document.createElement('canvas'); c.width = S; c.height = S;
  const ctx = c.getContext('2d')!;
  const gp = ctx.createPattern(gold, 'repeat')!;
  const P = PIGMENTS;
  band(ctx, S, 0, 4, P.ink);
  band(ctx, S, 4, 4, P.bole);
  band(ctx, S, 8, 12, gp);
  band(ctx, S, 20, 4, P.ink);
  ctx.fillStyle = 'rgba(92,58,34,.5)';
  for (let t = 4; t < S; t += 8) for (const [x, y] of [[t, 14], [t, S - 14], [14, t], [S - 14, t]]) {
    if (inCorner(x, y, S, 24)) continue;
    ctx.beginPath(); ctx.arc(x, y, 1.5, 0, Math.PI * 2); ctx.fill();
  }
  for (const [x, y] of [[14, 14], [S - 14, 14], [14, S - 14], [S - 14, S - 14]]) {
    quatrefoil(ctx, x, y, 9); ctx.fillStyle = P.vermilion; ctx.fill(); ctx.lineWidth = 1.6; ctx.strokeStyle = P.ink; ctx.stroke();
  }
  return c;
}

/** Plate card (hero cards): 96² source, slice 24 → 8 px band; ink rim, vermilion pearl edge, gold tooling. */
function paintPlateCard(gold: HTMLCanvasElement, verm: HTMLCanvasElement): HTMLCanvasElement {
  const S = 96;
  const c = document.createElement('canvas'); c.width = S; c.height = S;
  const ctx = c.getContext('2d')!;
  const gp = ctx.createPattern(gold, 'repeat')!, vp = ctx.createPattern(verm, 'repeat')!;
  const P = PIGMENTS;
  band(ctx, S, 0, 3, P.ink);
  band(ctx, S, 3, 9, vp);
  band(ctx, S, 12, 3, P.ink);
  band(ctx, S, 15, 6, gp);
  band(ctx, S, 21, 3, P.ink);
  ctx.fillStyle = P.leadWhite;
  for (let t = 6; t < S; t += 12) for (const [x, y] of [[t, 7.5], [t, S - 7.5], [7.5, t], [S - 7.5, t]]) {
    if (inCorner(x, y, S, 24)) continue;
    ctx.beginPath(); ctx.arc(x, y, 2, 0, Math.PI * 2); ctx.fill();
  }
  for (const [x, y] of [[12, 12], [S - 12, 12], [12, S - 12], [S - 12, S - 12]]) {
    quatrefoil(ctx, x, y, 10); ctx.fillStyle = gp; ctx.fill(); ctx.lineWidth = 1.6; ctx.strokeStyle = P.ink; ctx.stroke();
  }
  return c;
}

// ── bake ──────────────────────────────────────────────────────────────────────────────────────────────────────────

let baked: BakeResult | null = null;
let inflight: Promise<BakeResult> | null = null;

function toCanvas(bytes: Uint8ClampedArray, size: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = size; canvas.height = size;
  canvas.getContext('2d')!.putImageData(new ImageData(bytes as unknown as Uint8ClampedArray<ArrayBuffer>, size, size), 0, 0);
  return canvas;
}

const toUrl = (canvas: HTMLCanvasElement): Promise<string> =>
  new Promise((res, rej) => canvas.toBlob((b) => (b ? res(URL.createObjectURL(b)) : rej(new Error('toBlob failed'))), 'image/png'));

/** Frame tiles → CSS vars (F5's painted frames override them when the manifest provides them, see `art/ArtImage.ts`). */
const FRAME_VARS: Record<string, string> = { gilt: '--ui-frame-gilt-baked', plateBand: '--ui-plate-band-baked', plateCard: '--ui-plate-card-baked' };

/** Sets the material custom properties from tile URLs (also the hook for art §4.11's GPU bake). */
export function setMaterialUrls(urls: Record<string, string>, root: HTMLElement = document.documentElement): void {
  for (const [cssVar, name] of Object.entries(VARS)) if (urls[name]) root.style.setProperty(cssVar, `url("${urls[name]}")`);
  for (const [name, cssVar] of Object.entries(FRAME_VARS)) if (urls[name]) root.style.setProperty(cssVar, `url("${urls[name]}")`);
}

/** Bakes every tile and frame once per session (seeded), sets the CSS custom properties, resolves with the timing. */
export function bakeMaterials(seed = 'marg-materials', root: HTMLElement = document.documentElement): Promise<BakeResult> {
  if (baked) { setMaterialUrls(baked.urls, root); return Promise.resolve(baked); }
  if (inflight) return inflight;
  const t0 = performance.now();
  const rng = new Rng(seed);
  const canvases: Record<string, HTMLCanvasElement> = {};
  for (const [name, t] of Object.entries(TILES)) canvases[name] = toCanvas(t.paint(rng.stream(name), t.size), t.size);
  canvases.gilt = paintGilt(canvases.gold, canvases.lapis);
  canvases.plateBand = paintPlateBand(canvases.gold);
  canvases.plateCard = paintPlateCard(canvases.gold, canvases.vermilion);
  const paintMs = performance.now() - t0;
  inflight = Promise.all(Object.entries(canvases).map(async ([name, cv]) => [name, await toUrl(cv)] as const)).then((pairs) => {
    const urls = Object.fromEntries(pairs);
    baked = { ms: Math.round(paintMs), urls };
    setMaterialUrls(urls, root);
    document.documentElement.dataset.materials = 'baked';
    return baked;
  });
  return inflight;
}

/** The last bake (null before the first one finished): `__marg.ui.perf().bakeMs` reads `ms`. */
export function materialBake(): BakeResult | null { return baked; }

/** Pure painters for tests and the art lab: returns RGBA bytes of one tile. */
export function paintTile(name: keyof typeof TILES, seed = 'marg-materials'): { size: number; bytes: Uint8ClampedArray } {
  const t = TILES[name];
  return { size: t.size, bytes: t.paint(new Rng(seed).stream(name), t.size) };
}
